import { Injectable } from '@angular/core';
import { HTTPStatusCodes } from '@asamco/utils';
import aes from 'crypto-js/aes';
import cryptoCore from 'crypto-js/core';
import utf8 from 'crypto-js/enc-utf8';
import pkcs7 from 'crypto-js/pad-pkcs7';
import { jwtDecode } from 'jwt-decode';
import { from, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { AppSettings } from '../configuration/appsettings';
import { BackEndApiType } from '../configuration/backend-api-type';
import { AuthenticationProvider, AuthenticationResult, LoginCredentials, LogoffParameter, UserSession } from './authentication.interfaces';

/**
 * Webapi authentication
 */
@Injectable({
	providedIn: 'root',
	deps: [AppSettings],
})
export class WebApiAuthenticationProvider implements AuthenticationProvider {
	backendType: BackEndApiType = BackEndApiType.WebApi;

	/**
	 *
	 */
	constructor(private appSettings: AppSettings) {}

	/**
	 * Encrypts the password for webapi login
	 */
	private encryptPass(password) {
		const key = utf8.parse('Q_Dp[6*ZxvZk^Kna');
		const iv = utf8.parse('%J52l74N.TgyhxKy');
		const encrypted = aes.encrypt(utf8.parse(password), key, {
			// keySize: 128 / 8,
			iv: iv,
			mode: cryptoCore.mode.CBC,
			padding: pkcs7,
		});
		return '' + encrypted;
	}

	initialize() {
		// Nothing ot initialize
	}

	restorePreviousSession(storedSessionData: UserSession): Observable<AuthenticationResult> {
		if (storedSessionData.backend == null) {
			return of(<AuthenticationResult>{
				message: 'Cannot restore previous session, invalid data.',
				success: false,
			});
		}

		const result = <AuthenticationResult>{
			token: storedSessionData.backend.token,
			user: jwtDecode(storedSessionData.backend.token),
			server: {
				id: storedSessionData.backend.serverId,
				address: storedSessionData.backend.baseUrl,
				apiType: storedSessionData.backend.apiType,
				customerId: storedSessionData.backend.customerId,
				name: storedSessionData.backend.name,
			},
			authProvider: this,
			databaseId: storedSessionData.backend.databaseId,
			success: true,
		};

		return of(result);
	}

	login(loginCreds: LoginCredentials): Observable<AuthenticationResult> {
		const loginUrl = loginCreds.activeServer.address + this.appSettings.settings.serverSettings[this.backendType].loginUrl;

		const loginData = {
			username: loginCreds.username,
			password: this.encryptPass(loginCreds.password),
			databaseId: loginCreds.databaseId,
			serverId: loginCreds.activeServer.id,
			kickOtherSessions: loginCreds.kickOther,
		};

		// use fetch here, so the http interceptor doesn't mess up userlogin or tests.
		const postProm = fetch(loginUrl, {
			method: 'POST',
			mode: 'cors',
			cache: 'no-cache',
			headers: {
				'Content-Type': 'application/json; charset=utf-8',
			},
			redirect: 'follow', // manual, *follow, error
			referrer: 'no-referrer', // no-referrer, *client
			body: JSON.stringify(loginData),
		})
			.then(async response => {
				if (response.status === HTTPStatusCodes.OK) {
					const textData = await response.text(); // text instead of JSON. API returns text
					return {
						httpStatusCode: response.status,
						token: textData,
						user: jwtDecode(textData),
						server: loginCreds.activeServer,
						authProvider: this,
						databaseId: loginCreds.databaseId,
						success: true,
					};
				} else if (response.status === HTTPStatusCodes.CONFLICT) {
					return {
						httpStatusCode: response.status,
						server: loginCreds.activeServer,
						authProvider: this,
						databaseId: loginCreds.databaseId,
						success: false,
						message: 'User is already logged in somewhere else.',
					};
				}
			})
			.catch(err => {
				return {
					httpStatusCode: err.status,
					server: loginCreds.activeServer,
					authProvider: this,
					databaseId: loginCreds.databaseId,
					success: false,
					message: err,
				};
			});

		return <any>from(postProm);
	}

	/**
	 * Whether the stored session is still valid
	 * @param storedSessionData
	 */
	getIsAuthorized(storedSessionData: UserSession): Observable<boolean> {
		// There should be a better way... now it will only check it was not a b2c login
		return of(Boolean(storedSessionData && storedSessionData.user && storedSessionData.user['oid'] == null));
	}

	logoff(): Observable<void> {
		throw new Error('Method not implemented.');
	}

	getLogoffParameters(): LogoffParameter[] {
		return [];
	}
}
